import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, Headphones, FileText, BookOpen, Receipt, Calendar } from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";

/** Server-synced audio player wrapper: reads and writes reading_progress for kind=audio. */
function LibraryAudio({ book, streamUrl, downloadUrl, initialSec }) {
  const saveTimer = useMemo(() => ({ id: null }), []);
  const onTime = (audioEl) => {
    clearTimeout(saveTimer.id);
    saveTimer.id = setTimeout(() => {
      const total = Math.floor(audioEl.duration || book.audio_duration_sec || 0);
      const cur = Math.floor(audioEl.currentTime || 0);
      if (!total || cur < 3) return;
      api.post(`/reading-progress/${book.id}`, {
        kind: "audio",
        location: { audio_sec: cur, percent: Math.round((cur / total) * 1000) / 10 },
        total: { seconds: total },
      }).catch(() => {});
    }, 4000);
  };
  return (
    <AudioPlayer
      src={streamUrl}
      storageKey={`aperture_audio_pos_${book.id}`}
      initialSeek={initialSec}
      onTimeUpdate={onTime}
      label={<><Headphones className="w-3 h-3 inline mr-1"/>Audiobook — resume where you left off</>}
      testid={`library-audio-player-${book.slug}`}
    />
  );
}

export default function Library() {
  const { user, loading } = useAuth();
  const [data, setData] = useState({ orders: [], ebooks: [] });
  const [progress, setProgress] = useState([]); // [{ebook_id, kind, location, total}]

  useEffect(() => {
    if (!user) return;
    api.get("/library").then((r) => setData(r.data)).catch(() => {});
    api.get("/reading-progress").then((r) => setProgress(r.data)).catch(() => setProgress([]));
  }, [user]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-24 text-navy/60">Loading…</div>;
  if (!user) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="library-signin-prompt">
      <div className="eyebrow mb-3">Members Only</div>
      <h1 className="aperture-h1 text-4xl">Sign in to access your library.</h1>
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/login" data-testid="library-login-btn" className="btn-primary px-6 py-3 text-xs uppercase tracking-widest rounded-sm">Sign in</Link>
        <Link to="/register" data-testid="library-register-btn" className="border border-navy text-navy px-6 py-3 text-xs uppercase tracking-widest hover:bg-navy hover:text-white transition-colors duration-300 rounded-sm">Register</Link>
      </div>
    </div>
  );

  const grantsFor = (ebookId) => {
    let pdfSession = null, audioSession = null;
    for (const o of data.orders) {
      for (const item of (o.items || [])) {
        if (item.ebook_id !== ebookId) continue;
        const fmt = item.format || "ebook";
        if (!pdfSession && (fmt === "ebook" || fmt === "bundle")) pdfSession = o.session_id;
        if (!audioSession && (fmt === "audio" || fmt === "bundle")) audioSession = o.session_id;
      }
    }
    return { pdfSession, audioSession };
  };

  const progressFor = (ebookId, kind) => progress.find(p => p.ebook_id === ebookId && p.kind === kind);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="library-page">
      <div className="eyebrow mb-3">Welcome back, {user.name || user.email}</div>
      <h1 className="aperture-h1 text-5xl mb-14">My Library.</h1>

      {data.ebooks.length === 0 ? (
        <div className="text-center py-24" data-testid="library-empty">
          <p className="text-navy/60 mb-6">No purchases yet.</p>
          <Link to="/catalog" className="btn-primary px-6 py-3 text-xs uppercase tracking-widest rounded-sm">Browse Catalog</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {data.ebooks.map((b) => {
            const { pdfSession, audioSession } = grantsFor(b.id);
            const authTok = (() => { try { return localStorage.getItem("aperture_token") || ""; } catch { return ""; } })();
            const tokQs = authTok ? `?token=${encodeURIComponent(authTok)}` : "";
            // `&v=<ver>` pins each URL to the current file — when the admin
            // re-uploads a PDF/MP3, `pdf_ver` / `audio_ver` change and the
            // browser drops any cached bytes for the previous version.
            const pdfV = b.pdf_ver ? `&v=${encodeURIComponent(b.pdf_ver)}` : "";
            const audioV = b.audio_ver ? `&v=${encodeURIComponent(b.audio_ver)}` : "";
            const streamUrl = audioSession ? `${API}/stream-audio/${audioSession}/${b.id}${tokQs}${audioV}` : null;
            const downloadAudioUrl = audioSession ? `${API}/download-audio/${audioSession}/${b.id}${tokQs}${audioV}` : null;
            const downloadPdfUrl = pdfSession && b.has_pdf ? `${API}/download/${pdfSession}/${b.id}${tokQs}${pdfV}` : null;
            const readProg = progressFor(b.id, "ebook");
            const audioProg = progressFor(b.id, "audio");
            const readLabel = readProg?.location?.page ? `Continue Reading · p. ${readProg.location.page}${readProg.total?.pages ? ` of ${readProg.total.pages}` : ""}` : "Read Now";

            return (
              <div key={b.id} data-testid={`library-book-${b.slug}`} className="book-card bg-white p-5 rounded-sm shadow-xl border" style={{ borderColor: "rgba(10,37,64,0.08)" }}>
                <div className="flex gap-5">
                  <div className="w-24 h-32 bg-[#F7F5F0] rounded-sm flex items-center justify-center p-2 flex-shrink-0">
                    <img src={b.cover_image_url} alt={b.title} className="max-w-full max-h-full w-auto h-auto object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow mb-1">{b.category}</div>
                    <div className="font-serif text-lg text-navy leading-tight">{b.title}</div>
                    {b.narrator && audioSession && (
                      <div className="mt-1 text-xs text-navy/60">Narrated by {b.narrator}</div>
                    )}
                    {readProg?.location?.percent != null && (
                      <div className="mt-3">
                        <div className="h-1 bg-navy/10 rounded-full overflow-hidden">
                          <div className="h-full bg-gold" style={{ width: `${Math.min(100, readProg.location.percent)}%` }} data-testid={`library-progress-${b.slug}`}/>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  {pdfSession && b.has_pdf && (
                    <div className="flex flex-wrap items-center gap-4">
                      <Link to={`/read/${b.slug}`} data-testid={`library-read-${b.slug}`}
                        className="inline-flex items-center gap-2 btn-primary px-4 py-2 text-xs uppercase tracking-widest rounded-sm">
                        <BookOpen className="w-3.5 h-3.5"/> {readLabel}
                      </Link>
                      <a href={downloadPdfUrl} data-testid={`library-download-${b.slug}`}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy hover:text-gold transition-colors">
                        <FileText className="w-3.5 h-3.5" /> Download PDF
                      </a>
                    </div>
                  )}
                  {pdfSession && !b.has_pdf && (
                    <div className="text-xs text-navy/50">PDF being finalized — check back soon.</div>
                  )}

                  {audioSession && streamUrl && b.has_audio_file && (
                    <div className="pt-3 border-t" style={{ borderColor: "rgba(10,37,64,0.08)" }} data-testid={`library-audio-block-${b.slug}`}>
                      <LibraryAudio book={b} streamUrl={streamUrl} downloadUrl={downloadAudioUrl} initialSec={audioProg?.location?.audio_sec} />
                      <div className="mt-3 flex items-center gap-4">
                        {audioProg?.location?.audio_sec != null && (
                          <span className="text-[11px] uppercase tracking-widest text-navy/50" data-testid={`library-audio-continue-${b.slug}`}>
                            Resume · {fmtTime(audioProg.location.audio_sec)}
                            {audioProg.total?.seconds ? ` of ${fmtTime(audioProg.total.seconds)}` : ""}
                          </span>
                        )}
                        <a href={downloadAudioUrl} data-testid={`library-download-audio-${b.slug}`}
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy hover:text-gold transition-colors">
                          <Download className="w-3.5 h-3.5" /> Download MP3
                        </a>
                      </div>
                    </div>
                  )}
                  {audioSession && !b.has_audio_file && (
                    <div className="text-xs text-navy/50 pt-3 border-t" style={{ borderColor: "rgba(10,37,64,0.08)" }}>
                      Audiobook being finalized — check back soon.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <PurchaseHistory orders={data.orders} />
    </div>
  );
}

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const r = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

/**
 * Chronological list of every paid order for the signed-in user. Each row
 * offers a physical-receipt-style PDF download via /api/orders/{sid}/receipt.pdf.
 */
function PurchaseHistory({ orders }) {
  if (!orders?.length) return null;
  const sorted = [...orders].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const authTok = (() => { try { return localStorage.getItem("aperture_token") || ""; } catch { return ""; } })();
  const receiptHref = (sid) => `${API}/orders/${sid}/receipt.pdf${authTok ? `?token=${encodeURIComponent(authTok)}` : ""}`;
  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  return (
    <section className="mt-24" data-testid="purchase-history-section">
      <div className="eyebrow mb-3">History</div>
      <h2 className="aperture-h1 text-3xl md:text-4xl mb-8">Purchase History.</h2>
      <div className="space-y-4">
        {sorted.map((o) => (
          <div key={o.session_id}
               data-testid={`order-row-${o.session_id}`}
               className="bg-white p-5 md:p-6 shadow-lg rounded-sm flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
               style={{ border: "1px solid rgba(10,37,64,0.06)" }}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-navy/50">
                <Calendar className="w-3 h-3" /> {fmtDate(o.created_at)}
                <span className="ml-2 px-2 py-0.5 bg-gold/15 text-gold rounded-sm">
                  {(o.payment_status || "paid").toUpperCase()}
                </span>
              </div>
              <div className="mt-2 font-serif text-lg text-navy truncate">
                {(o.items || []).map((i) => i.title).join(" · ") || "Order"}
              </div>
              <div className="text-xs text-navy/60 mt-0.5">
                {(o.items || []).length} item{(o.items || []).length === 1 ? "" : "s"} ·
                Order #{(o.session_id || "").slice(-10)}
              </div>
            </div>
            <div className="text-right md:min-w-[140px]">
              <div className="font-serif text-xl text-navy">${Number(o.total || 0).toFixed(2)}</div>
              <a href={receiptHref(o.session_id)}
                 data-testid={`order-receipt-${o.session_id}`}
                 target="_blank" rel="noopener noreferrer"
                 className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy hover:text-gold transition-colors">
                <Receipt className="w-3.5 h-3.5" /> Receipt PDF
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
